import React, { useMemo } from "react";
import {
  Alert,
  Checkbox,
  colors,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import {
  EXPORT_DATA_TYPES,
  EXPORT_FIELDS_INCLUSION_TYPES,
  EXPORT_INCLUSION_TYPES,
  EXPORT_RELATED_FILTERS,
  ExportFieldConfiguration,
} from "./types";
import { getFilterRadioOption, getCommonExportRatioOptions } from "./utils";
import { ExportDataTypeInclusionSelector } from "./ExportDataTypeInclusionSelector";
import { useFaiSelectedRenderer } from "./hooks/useFaiSelectedRenderer";
import { FieldConfigurationSelector } from "./FieldConfigurationSelector";
import { faiExportFields } from "./exportConfigurationFields";

interface Props {
  isFaiFilterEmpty: boolean;
  inclusion: EXPORT_INCLUSION_TYPES;
  setInclusion?: (value: EXPORT_INCLUSION_TYPES) => void;
  faiDecisionMakersAndInfluencersInclusion: boolean;
  setFaiDecisionMakersAndInfluencersInclusion: (value: boolean) => void;
  faiProductIds?: number[];
  setFaiProductIds?: (productIds: number[]) => void;
  faiDepartmentIds?: string[];
  faiRoleIds?: string[];
  setFaiDepartmentAndRoleIds?: (
    departmentIds: string[],
    roleIds: string[]
  ) => void;
  universeEnabled?: boolean;
  /** OppGen shows the decision-makers option only with enableNewFaiSheet. */
  showDecisionMakersOption?: boolean;
  faiFieldConfiguration?: ExportFieldConfiguration;
  setFaiFieldConfiguration?: (cfg: ExportFieldConfiguration) => void;
  enforceMandatoryFields?: boolean;
}

export const FAIExportOptions: React.FC<Props> = ({
  inclusion,
  setInclusion,
  isFaiFilterEmpty,
  setFaiDecisionMakersAndInfluencersInclusion,
  faiDecisionMakersAndInfluencersInclusion,
  faiProductIds = [],
  setFaiProductIds,
  faiDepartmentIds = [],
  faiRoleIds = [],
  setFaiDepartmentAndRoleIds,
  universeEnabled,
  showDecisionMakersOption = true,
  faiFieldConfiguration,
  setFaiFieldConfiguration,
  enforceMandatoryFields,
}) => {
  const faiNavItemOptions = useMemo(() => {
    const filterOption = getFilterRadioOption(
      EXPORT_DATA_TYPES.FAI,
      EXPORT_RELATED_FILTERS.PRODUCTS,
      isFaiFilterEmpty
    );
    const selectedOption = {
      label: "Include only selected products",
      value: EXPORT_INCLUSION_TYPES.SELECTED,
    };
    const allOption = {
      label: "Include all FAI data (Universe)",
      value: EXPORT_INCLUSION_TYPES.ALL,
    };
    const [, noneOption] = getCommonExportRatioOptions(EXPORT_DATA_TYPES.FAI);

    // Matches OppGen's order: Filters, None, All (universe only), Selected — Selected is always last.
    return [
      filterOption,
      noneOption,
      ...(universeEnabled ? [allOption] : []),
      selectedOption,
    ];
  }, [isFaiFilterEmpty, universeEnabled]);

  const isFAIFilterEmptyOrNone = inclusion === EXPORT_INCLUSION_TYPES.NONE;

  const renderOptionContent = useFaiSelectedRenderer(
    inclusion,
    faiProductIds,
    (ids) => setFaiProductIds?.(ids),
    faiDepartmentIds,
    faiRoleIds,
    (departmentIds, roleIds) =>
      setFaiDepartmentAndRoleIds?.(departmentIds, roleIds),
    !!universeEnabled
  );

  return (
    <Stack spacing={1} sx={{ width: "70%", paddingTop: "40px" }}>
      <Stack>
        <Typography variant="h5" fontWeight={500} fontSize={16}>
          Functional Area Intelligence
        </Typography>
        <Typography variant="caption">
          Customize what content is included when exporting.
        </Typography>
      </Stack>
      <Stack>
        {isFaiFilterEmpty && !universeEnabled && (
          <Alert
            sx={{
              mt: 1,
              mb: 2,
              mr: 3,
              bgcolor: "white",
              fontSize: 12,
              border: "1px solid",
              borderColor: colors.lightBlue[100],
            }}
            severity="info"
          >
            Functional Area Intelligence (FAI) requires product filters with set
            department/role refinements. To include FAI in your export, please
            update the filters on the product filters section.
          </Alert>
        )}
      </Stack>
      <ExportDataTypeInclusionSelector
        dataType={EXPORT_DATA_TYPES.FAI}
        options={faiNavItemOptions}
        inclusion={inclusion}
        setInclusion={(inclusion: EXPORT_INCLUSION_TYPES) => {
          setInclusion?.(inclusion);
          //when option "Do not include any Functional Area Intelligence" is checked
          if (inclusion === EXPORT_INCLUSION_TYPES.NONE) {
            //deselect  "Only include locations with Decision Makers or Influencers present" checkbox
            setFaiDecisionMakersAndInfluencersInclusion(false);
          }
        }}
        renderOptionContent={renderOptionContent}
      />
      {showDecisionMakersOption && (
        <FormControlLabel
          control={
            <Checkbox
              id="fai-export-DM-Influencer-inclusion-checkbox"
              checked={faiDecisionMakersAndInfluencersInclusion}
              disabled={isFAIFilterEmptyOrNone}
              onClick={() => {
                setFaiDecisionMakersAndInfluencersInclusion(
                  !faiDecisionMakersAndInfluencersInclusion
                );
              }}
              sx={{ pl: 0 }}
            />
          }
          label={
            <Typography color={isFAIFilterEmptyOrNone ? colors.grey[300] : ""}>
              Only include locations with Decision Makers or Influencers present
            </Typography>
          }
          sx={{ mt: "0 !important" }}
        />
      )}
      {universeEnabled && (
        // No firmographicsFields prop here — matches OppGen, which excludes FAI
        // (along with Contacts, Time Series, Mention Time Series) from the
        // generic Firmographics sub-section. This is intentional, not an omission.
        <FieldConfigurationSelector
          fields={faiExportFields}
          value={
            faiFieldConfiguration ?? {
              inclusionType: EXPORT_FIELDS_INCLUSION_TYPES.ALL,
              fields: [],
            }
          }
          onChange={setFaiFieldConfiguration ?? (() => {})}
          title="FAI Fields"
          checkboxLabel="Export All FAI Fields"
          // Matches OppGen: the note + force-injection of company_group_id/product_id
          // are only active behind the universe_enforce_mandatory_fields_for_tracking flag.
          requiredField={
            enforceMandatoryFields
              ? {
                  values: ["company_group_id", "product_id"],
                  note: "Note: Company Group ID and Product ID will always be included in your export for proper install identification.",
                }
              : undefined
          }
          disabled={isFAIFilterEmptyOrNone}
        />
      )}
    </Stack>
  );
};
